import React, { useState, useEffect } from 'react';
import "../../assets/css/Driver.css";
import { FaTruck, FaChevronRight, FaSortAmountUpAlt, FaInfoCircle, FaTrashAlt, FaPlus, FaSearch } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { specialBookingsAPI } from '../../api/apiClient';
import PaginationComponent from '../common/PaginationComponent';
import { Spinner, Alert, Button, Modal, Badge, Form, InputGroup, Row, Col } from "react-bootstrap";
import Swal from 'sweetalert2';

const SpecialBooking = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("active");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
  });
  const [counts, setCounts] = useState({
    active: 0,
    assigned: 0,
    received: 0,
    completed: 0
  });

  // Fetch special bookings based on tab with search
  const fetchSpecialBookings = async (page = 1, search = searchTerm, tab = activeTab) => {
    try {
      setLoading(true);
      setError('');

      let response;
      const params = {
        page,
        per_page: pagination.per_page,
        search,
      };

      switch (tab) {
        case "completed":
          response = await specialBookingsAPI.completed(params);
          break;
        case "received":
          response = await specialBookingsAPI.received(params);
          break;
        case "assigned":
          response = await specialBookingsAPI.assigned(params);
          break;
        default:
          response = await specialBookingsAPI.index(params);
      }

      if (response.success) {
        const fetchedData = response.data.data || response.data;
        setData(fetchedData);

        // Update counts from response
        if (response.data.counts) {
          setCounts({
            all: response.data.counts.all || 0,
            pending: response.data.counts.pending || 0,
            assigned: response.data.counts.assigned || 0,
            received: response.data.counts.received || 0,
            completed: response.data.counts.completed || 0
          });
        }

        setPagination(response.data.meta || {
          current_page: page,
          last_page: response.data.last_page || 1,
          per_page: response.data.per_page || 10,
          total: response.data.total || 0,
        });
      } else {
        setError(response.message || t('failed_to_fetch_data'));
      }
    } catch (err) {
      setError(t('failed_to_fetch_data'));
      console.error('Error fetching special bookings:', err);
    } finally {
      setLoading(false);
    }
  };

  // Debounced Search Effect
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchTerm !== undefined) {
        fetchSpecialBookings(1, searchTerm, activeTab);
      }
    }, 1500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  // Fetch counts for all tabs
  const fetchAllCounts = async () => {
    try {
      const response = await specialBookingsAPI.index({ per_page: 1 });
      if (response.success && response.data.counts) {
        setCounts(response.data.counts);
      }
    } catch (err) {
      console.error('Error fetching counts:', err);
    }
  };


  useEffect(() => {
    fetchSpecialBookings(1, searchTerm);
    fetchAllCounts();
  }, [activeTab]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchSpecialBookings(1, searchTerm);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchTerm('');
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setLoading(true);
      const response = await specialBookingsAPI.delete(deleteId);
      
      if (response.success) {
        Swal.fire({
          icon: 'success',
          title: t('success'),
          text: t('booking_deleted_successfully'),
          timer: 3000,
          showConfirmButton: false
        });
        fetchSpecialBookings(pagination.current_page);
        fetchAllCounts();
      } else {
        setError(response.message || t('failed_to_delete_booking'));
      }
    } catch (err) {
      setError(t('failed_to_delete_booking'));
      console.error('Error deleting booking:', err);
    } finally {
      setLoading(false);
      setShowDeleteModal(false);
      setDeleteId(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  const handleViewDetails = (id) => {
    navigate(`/admin/special-bookings/details/${id}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getProgressPercentage = (booking) => {
    if (!booking) return 0;
    
    const statusOrder = ['pending','assigned', 'received', 'completed'];
    const currentStatusIndex = statusOrder.indexOf(booking.status);
    return Math.round((currentStatusIndex / (statusOrder.length - 1)) * 100);
  };

  const getProgressColor = (percentage) => {
    if (percentage < 40) return "#e74c3c"; 
    if (percentage < 80) return "#f39c12";
    return "#2ecc71";
  };


  // const getStatusBadge = (status) => {
  //   const statusConfig = {
  //     assigned: { class: 'status-assigned', text: t('assigned') },
  //     received: { class: 'status-received', text: t('received') },
  //     completed: { class: 'status-completed', text: t('completed') }
  //   };
    
  //   const config = statusConfig[status] || { class: 'status-pending', text: status };
  //   return `<span class="status-badge ${config.class}">${config.text}</span>`;
  // };

  const getStatusBadge = (status) => {
    const statusConfig = {
      assigned: { variant: 'warning', text: t('assigned') },
      received: { variant: 'info', text: t('received') },
      completed: { variant: 'success', text: t('completed') },
      pending: { variant: 'secondary', text: t('pending') }
    };
    
    const config = statusConfig[status] || { variant: 'light', text: status };
    return <Badge bg={config.variant}>{config.text}</Badge>;
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  if (loading && data.length === 0) {
    return (
      <div className="driver-page">
        <div className="text-center py-4">
          <Spinner animation="border" />
          <p className="mt-2">{t('loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="driver-page">
      <div className="top-bar">
        {/* <Form onSubmit={handleSearch} className="flex-grow-1 me-3">
          <InputGroup>
            <Form.Control
              placeholder={t("search_by_booking_id")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Button variant="outline-secondary" type="submit">
              <FaSearch />
            </Button>
          </InputGroup>
        </Form> */}

          <div className="d-flex gap-2">
            {/* Search Bar */}
            <Row className="mb-3">
              <Col md={12}>
                <InputGroup>
                  <InputGroup.Text><FaSearch /></InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder={t('search_booking_id')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {searchTerm && (
                    <Button 
                      variant="outline-secondary" 
                      onClick={clearSearch}
                      title={t("reset_search")}
                    >
                      ✕
                    </Button>
                  )}
                </InputGroup>
              </Col>
            </Row>
          </div>
        
        <div className="d-flex flex-wrap-wrap justify-content-between align-items-center flex-wrap">
          <div className="tabs">
             <button
                className={activeTab === "active" ? "active" : ""}
                onClick={() => handleTabChange("active")}
              >
                <span className="count-badge">{counts.all}</span>
                {t("all_bookings")}
              </button>
              <button
                className={activeTab === "assigned" ? "active" : ""}
                onClick={() => handleTabChange("assigned")}
              >
                <span className="count-badge">{counts.assigned}</span>
                {t("assigned_bookings")}
              </button>
              <button
                className={activeTab === "received" ? "active" : ""}
                onClick={() => handleTabChange("received")}
              >
                <span className="count-badge">{counts.received}</span>
                {t("received_bookings")}
              </button>
              <button
                className={activeTab === "completed" ? "active" : ""}
                onClick={() => handleTabChange("completed")}
              >
                <span className="count-badge">{counts.completed}</span>
                {t("completed_bookings")}
              </button>
          </div>
          
          <div className="d-flex gap-2">
            <Button 
              className="add-btn btn btn-primary"
              onClick={() => navigate('/admin/special-bookings/new')}
            >
              <FaPlus /> {t('Neu_hinzufügen')}
            </Button>
            {/* <div className="sort-by">
              <label>{t('sort_by')}:</label>
              <span className="sort-status">
                <FaSortAmountUpAlt className="sort-icon" />
                <strong> {t('status_date')}</strong>
              </span>
            </div> */}
          </div>
        </div>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

       <div className="orders">
        {data.length === 0 ? (
          <div className="text-center py-5">
            <h4>{t('no_bookings_found')}</h4>
            {/* <p className="text-muted">{t('no_bookings_description')}</p> */}
          </div>
        ) : (
          data.map((booking) => (
            <div className="order-card" key={booking.id}>
              <div className="order-header">
                <span className="order-id">
                  <strong>
                    {searchTerm ? (
                        <span 
                          dangerouslySetInnerHTML={{
                            __html: booking.booking_id?.replace(
                              new RegExp(searchTerm, 'gi'), 
                              match => `<mark class="bg-warning">${match}</mark>`
                            )
                          }} 
                        />
                      ) : (
                        booking.booking_id
                      )}
                  </strong>
                </span>
                <span className="driver">
                  <strong>{t("driver")}:</strong> {booking.order?.driver_name || t('not_assigned')}
                </span>
                <span className="vehicle">
                  <FaTruck />
                  &nbsp;
                  <small>{booking.order?.vehicle_type}</small>
                </span>
                <span className="created">
                  <strong>
                     {t("booking.created_on")}:
                  </strong>{" "}{formatDate(booking.created_at)}</span>
                <span className="price">
                  {getStatusBadge(booking.order?.status || 'pending')}
                </span>
              </div>

              <div className="order-body">
                <div className="left d-flex flex-wrap-wrap justify-content-between flex-wrap">
                  <div className="d-flex flex-column">
                    <strong>{t("booking.loading_company")}:</strong> 
                    {booking.loading_company?.[0] || t('not_available')}
                  </div>
                  <div className="d-flex flex-column">
                    <strong>{t("booking.unloading_company")}:</strong> 
                    {booking.unloading_company?.[0] || t('not_available')}
                  </div>

                  <div className="delivery-path-vertical">
                    <div className="circle-label-vertical">
                      <span className="circle"></span>
                      <span className="label">{booking.booking_from?.[0]}</span>
                    </div>
                    <div className="line-container">
                      <div className="line-vertical"></div>
                    </div>
                    <div className="circle-label-vertical">
                      <span className="circle"></span>
                      <span className="label">{booking.booking_to?.[0]}</span>
                    </div>
                  </div>
                </div>
                <div className="right">
                  <div className="progress-bar-container mt-2">
                    <div className="progress-header d-flex justify-content-between align-items-center mb-1">
                      <strong>{t("progress")}:</strong>
                      <span className="progress-label">{getProgressPercentage(booking)}%</span>
                    </div>
                    
                    <div className="progress-bar-outer">
                      <div
                        className="progress-bar-inner"
                        style={{
                          width: `${getProgressPercentage(booking)}%`,
                          background: getProgressColor(getProgressPercentage(booking)),
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="order-footer">
                <div>
                  <strong>
                    {booking.order?.status === 'assigned' ? t("booking.assigned_on") : 
                     booking.order?.status === 'received' ? t("booking.received_on") : 
                     t("booking.completed_on")}
                  </strong>{" "}
                  {formatDate(booking.order?.assigned_date || booking.created_at)}
                </div>
                <div>
                  <strong>{t("distance")}:</strong> {booking.distance} | 
                  <strong> {t("duration")}:</strong> {booking.total_duration}
                </div>
                <div>
                  <strong>{t("booking.total_weight")}:</strong> {booking.total_weight}
                </div>

                <div className="action-buttons">
                  <Button
                    variant="outline-info"
                    size="sm"
                    onClick={() => handleViewDetails(booking.id)}
                    className="me-2"
                  >
                    <FaInfoCircle /> {t("details")}
                  </Button>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleDeleteClick(booking.id)}
                  >
                    <FaTrashAlt /> {t("delete")}
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {pagination.total > 0 && (
        <PaginationComponent
          currentPage={pagination.current_page}
          lastPage={pagination.last_page}
          onPageChange={fetchSpecialBookings}
          disabled={loading}
        />
      )}

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={handleDeleteCancel} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('confirm_deletion')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t('confirm_delete_booking')}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleDeleteCancel}>
            {t('cancel')}
          </Button>
          <Button variant="danger" onClick={handleDeleteConfirm} disabled={loading}>
            {loading ? t('deleting') : t('delete')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SpecialBooking;